/**
 * Icons — single source of truth untuk semua ikon aktivitas, tema,
 * avatar, dan ilustrasi kecil (misc) di app.
 *
 * Fase 3 redesign: menggantikan lib/emoji.ts. Semua ikon adalah PNG
 * 512px transparan hasil generate (GPT-Images-2 via codex-cli imagegen
 * skill) dengan gaya 3D polymer-clay — colorful, ramah anak, TANPA
 * unsur manusia maupun hewan (hard rule Doc 03).
 *
 * Alias tema extended (InsightCard dsb.) diarahkan ke ikon canonical
 * yang maknanya sama supaya jumlah asset tetap terjaga.
 */

import type { ImageSourcePropType } from 'react-native';

// ----------------------------------------------------------------------------
// Activity (label kanonik dari reference-dev / worksheet.activity_label)
// ----------------------------------------------------------------------------

export const ACTIVITY_ICON: Record<string, ImageSourcePropType> = {
  'Melingkari Gambar': require('../../assets/icons/activity-melingkari-gambar.png'),
  'Soal Campuran': require('../../assets/icons/activity-soal-campuran.png'),
  Analisa: require('../../assets/icons/activity-analisa.png'),
  Membandingkan: require('../../assets/icons/activity-membandingkan.png'),
  'Art and Craft': require('../../assets/icons/activity-art-and-craft.png'),
  'Menulis dan Menggambar': require('../../assets/icons/activity-menulis-dan-menggambar.png'),
  'Operasi Hitung': require('../../assets/icons/activity-operasi-hitung.png'),
  Mewarnai: require('../../assets/icons/activity-mewarnai.png'),
  'Menebalkan Garis': require('../../assets/icons/activity-menebalkan-garis.png'),
  Maze: require('../../assets/icons/activity-maze.png'),
  Mencocokkan: require('../../assets/icons/activity-mencocokkan.png'),
  Berhitung: require('../../assets/icons/activity-berhitung.png'),
  'Menghitung Angka': require('../../assets/icons/activity-berhitung.png'), // alias label quiz
  'Mengenal Huruf': require('../../assets/icons/activity-mengenal-huruf.png'),
  'Gunting Tempel': require('../../assets/icons/activity-gunting-tempel.png'),
  Menggunting: require('../../assets/icons/activity-menggunting.png'),
  'Menghubungkan Titik': require('../../assets/icons/activity-menghubungkan-titik.png'),
  Menulis: require('../../assets/icons/activity-menulis.png'),
};

const ACTIVITY_FALLBACK = require('../../assets/icons/activity-fallback.png');

/** Ikon untuk activity label; fallback tumpukan worksheet. */
export function activityIcon(activity: string | null): ImageSourcePropType {
  if (!activity) return ACTIVITY_FALLBACK;
  return ACTIVITY_ICON[activity] ?? ACTIVITY_FALLBACK;
}

// ----------------------------------------------------------------------------
// Tema — 14 PAUD canonical (nama = fp_tema.theme_name) + extended + alias
// ----------------------------------------------------------------------------

export const TEMA_ICON: Record<string, ImageSourcePropType> = {
  Kebutuhanku: require('../../assets/icons/tema-kebutuhanku.png'),
  'Diri Sendiri': require('../../assets/icons/tema-diri-sendiri.png'),
  Lingkungan: require('../../assets/icons/tema-lingkungan.png'),
  Tanaman: require('../../assets/icons/tema-tanaman.png'),
  'Air Udara Api': require('../../assets/icons/tema-air-udara-api.png'),
  'Alam Semesta': require('../../assets/icons/tema-alam-semesta.png'),
  'Alat Komunikasi': require('../../assets/icons/tema-alat-komunikasi.png'),
  Bepergian: require('../../assets/icons/tema-bepergian.png'),
  Profesi: require('../../assets/icons/tema-profesi.png'),
  'Tanah Airku': require('../../assets/icons/tema-tanah-airku.png'),
  Negaraku: require('../../assets/icons/tema-negaraku.png'),
  Kendaraan: require('../../assets/icons/tema-kendaraan.png'),
  Pekerjaan: require('../../assets/icons/tema-pekerjaan.png'),
  Rekreasi: require('../../assets/icons/tema-rekreasi.png'),
  // Extended (agregasi server / InsightCard)
  Keluarga: require('../../assets/icons/tema-keluarga.png'),
  'Kebutuhan Bunda PAUD': require('../../assets/icons/tema-bekal.png'),
  'Bangun Datar': require('../../assets/icons/tema-bangun-datar.png'),
  Cerita: require('../../assets/icons/tema-cerita.png'),
  'Eksperimen Sains': require('../../assets/icons/tema-sains.png'),
  'Tradisi & Budaya': require('../../assets/icons/tema-budaya.png'),
  // Alias — reuse ikon canonical dengan makna sama
  'Lingkungan Sekitar': require('../../assets/icons/tema-lingkungan.png'),
  Bilangan: require('../../assets/icons/activity-berhitung.png'),
  'Huruf & Kata': require('../../assets/icons/activity-mengenal-huruf.png'),
  'Bentuk & Warna': require('../../assets/icons/tema-bangun-datar.png'),
};

/** Ikon untuk nama tema; fallback sparkle. */
export function temaIcon(tema: string): ImageSourcePropType {
  return TEMA_ICON[tema] ?? MISC_ICON.sparkle;
}

// ----------------------------------------------------------------------------
// Avatar anak (edit-profil) — benda sehari-hari, TANPA hewan/manusia.
// ID 1-12 dipertahankan agar avatar yang tersimpan tetap kompatibel.
// ----------------------------------------------------------------------------

export const AVATAR_ICON: Record<number, ImageSourcePropType> = {
  1: require('../../assets/icons/avatar-tas.png'),
  2: require('../../assets/icons/avatar-buku.png'),
  3: require('../../assets/icons/avatar-pensil.png'),
  4: require('../../assets/icons/avatar-kotak-makan.png'),
  5: require('../../assets/icons/avatar-botol-minum.png'),
  6: require('../../assets/icons/avatar-payung.png'),
  7: require('../../assets/icons/avatar-jam.png'),
  8: require('../../assets/icons/avatar-lampu-meja.png'),
  9: require('../../assets/icons/avatar-sepatu.png'),
  10: require('../../assets/icons/avatar-gunting.png'),
  11: require('../../assets/icons/avatar-kotak-pensil.png'),
  12: require('../../assets/icons/avatar-kotak-tisu.png'),
};

export function avatarIcon(id: number): ImageSourcePropType {
  return AVATAR_ICON[id] ?? MISC_ICON.star;
}

// ----------------------------------------------------------------------------
// Misc — ilustrasi kecil lintas layar
// ----------------------------------------------------------------------------

export const MISC_ICON = {
  celebration: require('../../assets/icons/misc-celebration.png'),
  checkmark: require('../../assets/icons/misc-checkmark.png'),
  sparkle: require('../../assets/icons/misc-sparkle.png'),
  sun: require('../../assets/icons/misc-sun.png'),
  moon: require('../../assets/icons/misc-moon.png'),
  star: require('../../assets/icons/misc-star.png'),
  books: require('../../assets/icons/misc-books.png'),
  cards: require('../../assets/icons/misc-cards.png'),
  shoppingBag: require('../../assets/icons/misc-shopping-bag.png'),
  autoSelect: require('../../assets/icons/misc-auto-select.png'),
  skipDay: require('../../assets/icons/misc-skip-day.png'),
  actionMenu: require('../../assets/icons/misc-action-menu.png'),
  swipe: require('../../assets/icons/misc-swipe.png'),
} as const;

/** Ikon greeting berdasarkan waktu (pagi/siang/sore → matahari, malam → bulan). */
export function greetingIcon(timeOfDay: 'morning' | 'afternoon' | 'evening' | 'night'): ImageSourcePropType {
  return timeOfDay === 'night' ? MISC_ICON.moon : MISC_ICON.sun;
}
